import eyeOpen from "@assets/img/eye-open.svg";
import eyeClose from "@assets/img/eye-close.svg";
import { useSelector } from "react-redux";
import { useState } from "react";
import useMediaQuery from "@mui/material/useMediaQuery";
import IdIcon from "@assets/icons/IdIcon";
import EmailIcon from "@assets/icons/EmailIcon";
import LocationIcon from "@assets/icons/LocationIcon";
import WalletIcon from "@assets/icons/WalletIcon";
import IsVerifiedIcon from "@assets/icons/IsVerifiedIcon";
import { getVerificationStatus } from "@components/pages/MyProfile/components/ProfileTab/utils/profileFormatters";
import { useProfileUserData } from "@components/pages/MyProfile/components/ProfileTab/hooks/useProfileUserData";
import { useSessions } from "@components/pages/MyProfile/components/SettingsTab/Sessions/hooks/useSessions";
import { useTranslation } from "@helpers/translate";

import "./UserAccountSummary.sass";

const createHiddenMask = (value, fallbackLength = 6) => {
  const normalizedValue = String(value ?? "");
  const maskLength = normalizedValue.length || fallbackLength;

  return "*".repeat(maskLength);
};

const getSessionValue = (session, keys = [], fallback = "") => {
  if (!session || typeof session !== "object") {
    return fallback;
  }

  for (const key of keys) {
    const value = session[key];

    if (typeof value !== "undefined" && value !== null && value !== "") {
      return value;
    }
  }

  return fallback;
};

const createHiddenName = (value) => {
  const normalizedName = String(value ?? "").trim();
  const firstLetter = normalizedName.charAt(0);

  return firstLetter ? `${firstLetter}.` : "-";
};

const UserAccountSummary = () => {
  const { __ } = useTranslation();

  const [isPrivateDataVisible, setIsPrivateDataVisible] = useState(true);

  const isNarrowScreen = useMediaQuery("(max-width:1000px)");
  const iconSize = isNarrowScreen ? 16 : 16;

  const sessionsData = useSessions();

  const sessions = Array.isArray(sessionsData?.sessions)
    ? sessionsData.sessions
    : [];

  const sessionsLoading = Boolean(sessionsData?.loading);

  const {
    full_name: reduxFullName = "",
    email = "",
    id = "",
    wallet = {},
    display_id,
    avatar_url: reduxAvatarUrl,
  } = useSelector((state) => state.user.user || {});

  const { active_wallet } = useSelector((state) => state.wallet || {});
  const { ondato_status } = useProfileUserData();

  const isVerified =
    getVerificationStatus(ondato_status, __) === __("common.verified");

  const displayName =
    reduxFullName ||
    (typeof window !== "undefined"
      ? window.localStorage.getItem("user_full_name")
      : null) ||
    email ||
    "User";

  const displayedName = isPrivateDataVisible
    ? displayName
    : createHiddenName(displayName);

  const rawId =
    typeof display_id !== "undefined" && display_id !== null ? display_id : id;

  const balance =
    (active_wallet && typeof active_wallet.balance !== "undefined"
      ? active_wallet.balance
      : wallet?.balance) || 0;

  const formattedBalance = new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(balance) || 0);

  const currentSession =
    sessions.find(
      (session) =>
        session?.is_current === true ||
        session?.isCurrent === true ||
        session?.current === true,
    ) ||
    sessions.find(Boolean) ||
    null;

  const sessionIp = getSessionValue(currentSession, ["ip", "ip_address"], "");

  const displayedId = isPrivateDataVisible
    ? rawId != null && rawId !== ""
      ? String(rawId)
      : "-"
    : createHiddenMask(rawId);

  const displayedEmail = isPrivateDataVisible
    ? email || "-"
    : createHiddenMask(email);

  const displayedBalance = isPrivateDataVisible
    ? formattedBalance
    : createHiddenMask(formattedBalance);

  const displayedLocation = isPrivateDataVisible
    ? sessionIp || (sessionsLoading ? __("common.loading") : "-")
    : createHiddenMask(sessionIp, 12);

  const togglePrivateDataVisibility = () => {
    setIsPrivateDataVisible((previousValue) => !previousValue);
  };

  return (
    <div className="user-profile-dropdown__header">
      <div className="user-profile-dropdown__avatar">
        <div className="user-profile-dropdown__avatar-inner">
          {reduxAvatarUrl ? (
            <img src={reduxAvatarUrl} alt={displayName} />
          ) : (
            <svg
              width="32"
              height="32"
              viewBox="0 0 32 32"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M16.0121 14.3333C12.8867 14.3333 10.3454 11.7907 10.3454 8.66667C10.3454 5.54267 12.8867 3 16.0121 3C19.1374 3 21.6787 5.54267 21.6787 8.66667C21.6787 11.7907 19.1374 14.3333 16.0121 14.3333ZM16.0121 5C13.9894 5 12.3454 6.644 12.3454 8.66667C12.3454 10.6893 13.9894 12.3333 16.0121 12.3333C18.0347 12.3333 19.6787 10.6893 19.6787 8.66667C19.6787 6.644 18.0334 5 16.0121 5ZM21.3293 29H10.6708C7.44409 29 5.66669 27.2334 5.66669 24.0254C5.66669 20.4774 7.67469 16.3333 13.3334 16.3333H18.6667C24.3254 16.3333 26.3334 20.4761 26.3334 24.0254C26.3334 27.2334 24.556 29 21.3293 29ZM13.3334 18.3333C8.07602 18.3333 7.66669 22.6894 7.66669 24.0254C7.66669 26.1107 8.56542 27 10.6708 27H21.3293C23.4346 27 24.3334 26.1107 24.3334 24.0254C24.3334 22.6907 23.924 18.3333 18.6667 18.3333H13.3334Z"
                fill="white"
              />
            </svg>
          )}
        </div>
      </div>

      <div className="user-profile-dropdown__info">
        <p className="user-profile-dropdown__name">
          {displayedName}
          {isVerified && <IsVerifiedIcon />}
        </p>

        <div className="user-profile-dropdown__item">
          <IdIcon size={iconSize} />

          <p className="user-profile-dropdown__truncate">ID {displayedId}</p>

          <button
            type="button"
            className="user-profile-dropdown__toggle-id"
            onClick={togglePrivateDataVisibility}
            aria-label={
              isPrivateDataVisible ? "Hide private data" : "Show private data"
            }
          >
            <img src={isPrivateDataVisible ? eyeOpen : eyeClose} alt="" />
          </button>
        </div>

        <div className="user-profile-dropdown__item">
          <EmailIcon size={iconSize} />

          <p className="user-profile-dropdown__truncate">{displayedEmail}</p>
        </div>

        <div className="user-profile-dropdown__item">
          <WalletIcon size={iconSize} />

          <p className="user-profile-dropdown__truncate">${displayedBalance}</p>
        </div>

        <div className="user-profile-dropdown__item">
          <LocationIcon size={iconSize} />

          <p className="user-profile-dropdown__truncate">{displayedLocation}</p>
        </div>
      </div>
    </div>
  );
};

export default UserAccountSummary;
